import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Flex,
  Heading,
  Card,
  Box,
  DataList,
  Text,
  Grid,
  BoxProps,
  Badge,
  Code,
  Tabs,
  Skeleton,
  Table,
  Avatar,
  IconButton,
  Tooltip,
} from '@radix-ui/themes';
import {
  PersonIcon,
  ClockIcon,
  ActivityLogIcon,
  TokensIcon,
  CubeIcon,
  MixIcon,
  CopyIcon,
  FileTextIcon,
  BarChartIcon,
  PieChartIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  CheckCircledIcon,
  BellIcon,
  CounterClockwiseClockIcon,
} from '@radix-ui/react-icons';
import HashShortener from '../../components/HashShortener';
import CopyButton from '../../components/CopyButton';
import ViewButton from '../../components/ViewButton';
import InstrumentationChipGroup from '../../components/InstrumentationChip/InstrumentationChipGroup';
import InstrumentationPanel, {
  InstrumentationItem,
} from '../../components/InstrumentationChip/InstrumentationPanel';
import { ChipType } from '../../components/InstrumentationChip';
import { useGetAddressQuery } from '../../graphql/queries/GetAddress.generated';

// Reusable metadata item component
const MetadataItem = ({
  label,
  children,
  icon,
}: {
  label: string;
  children: React.ReactNode;
  icon?: React.ReactNode;
}) => (
  <DataList.Item align="center">
    <DataList.Label>
      <Flex align="center" gap="2">
        {icon && <Box color="gray">{icon}</Box>}
        <Text size="2" color="gray" weight="medium">
          {label}
        </Text>
      </Flex>
    </DataList.Label>
    <DataList.Value>{children}</DataList.Value>
  </DataList.Item>
);

// Reusable metadata card component
const MetadataCard = ({
  title,
  children,
  icon,
  gradient = false,
  ...otherProps
}: {
  title: string;
  children: React.ReactNode;
  icon?: React.ReactNode;
  gradient?: boolean;
} & BoxProps) => (
  <Card
    size="2"
    style={{
      background: gradient
        ? 'linear-gradient(135deg, var(--accent-2) 0%, var(--accent-3) 100%)'
        : undefined,
      border: gradient ? '1px solid var(--accent-6)' : undefined,
      height: '100%',
    }}
  >
    <Box height="100%" {...otherProps}>
      <Flex align="center" gap="2" mb="3">
        {icon && (
          <Box
            style={{
              padding: '4px',
              borderRadius: '6px',
              backgroundColor: gradient ? 'var(--accent-4)' : 'var(--accent-3)',
              color: gradient ? 'var(--accent-11)' : 'var(--accent-9)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: '24px',
              minHeight: '24px',
            }}
          >
            {icon}
          </Box>
        )}
        <Heading as="h3" size="3" trim="both" weight="medium">
          {title}
        </Heading>
      </Flex>
      <DataList.Root size="1">{children}</DataList.Root>
    </Box>
  </Card>
);

// Placeholder chart component
const PlaceholderChart = ({
  height = 200,
  label,
}: {
  height?: number;
  label: string;
}) => (
  <Box
    style={{
      height,
      background:
        'linear-gradient(135deg, var(--gray-2) 0%, var(--gray-3) 100%)',
      borderRadius: '8px',
      border: '1px dashed var(--gray-6)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <Flex direction="column" align="center" gap="2">
      <BarChartIcon width={32} height={32} color="var(--gray-8)" />
      <Text size="2" color="gray">
        {label}
      </Text>
    </Flex>
  </Box>
);

// Placeholder token row for the token balances table
const PlaceholderTokenRow = ({
  symbol,
  name,
  balance,
  value,
  change,
  contractAddress,
  onMonitor,
}: {
  symbol: string;
  name: string;
  balance: string;
  value: string;
  change: number;
  contractAddress?: string;
  onMonitor?: () => void;
}) => (
  <Table.Row>
    <Table.Cell>
      <Flex align="center" gap="2">
        <Avatar size="1" fallback={symbol[0]} color="gray" />
        <Flex direction="column">
          <Text size="2" weight="medium">
            {symbol}
          </Text>
          <Text size="1" color="gray">
            {name}
          </Text>
        </Flex>
      </Flex>
    </Table.Cell>
    <Table.Cell>
      <Text size="2" style={{ fontFamily: 'monospace' }}>
        {balance}
      </Text>
    </Table.Cell>
    <Table.Cell>
      <Text size="2" weight="medium">
        {value}
      </Text>
    </Table.Cell>
    <Table.Cell>
      <Flex align="center" gap="1">
        {change >= 0 ? (
          <ArrowUpIcon color="var(--green-11)" />
        ) : (
          <ArrowDownIcon color="var(--red-11)" />
        )}
        <Text size="2" color={change >= 0 ? 'green' : 'red'}>
          {Math.abs(change).toFixed(2)}%
        </Text>
      </Flex>
    </Table.Cell>
    <Table.Cell style={{ verticalAlign: 'middle' }}>
      <Flex gap="1" align="center" justify="end">
        <Tooltip content="View transfer history">
          <IconButton
            size="1"
            variant="ghost"
            color="gray"
            onClick={() =>
              console.log(`View history for ${symbol}`, contractAddress)
            }
          >
            <CounterClockwiseClockIcon width={14} height={14} />
          </IconButton>
        </Tooltip>
        <Tooltip content="Monitor token activity">
          <IconButton
            size="1"
            variant="ghost"
            color="amber"
            onClick={onMonitor}
          >
            <BellIcon width={14} height={14} />
          </IconButton>
        </Tooltip>
      </Flex>
    </Table.Cell>
  </Table.Row>
);

// Placeholder transaction row
const PlaceholderTxRow = ({
  hash,
  type,
  from,
  to,
  value,
  time,
}: {
  hash: string;
  type: 'in' | 'out' | 'self';
  from: string;
  to: string;
  value: string;
  time: string;
}) => (
  <Table.Row>
    <Table.Cell>
      <HashShortener hash={hash} link={`/transactions/${hash}`} />
    </Table.Cell>
    <Table.Cell>
      <Badge
        color={type === 'in' ? 'green' : type === 'out' ? 'red' : 'gray'}
        variant="soft"
        size="1"
      >
        {type === 'in' ? 'IN' : type === 'out' ? 'OUT' : 'SELF'}
      </Badge>
    </Table.Cell>
    <Table.Cell>
      <HashShortener hash={from} link={`/addresses/${from}`} />
    </Table.Cell>
    <Table.Cell>
      <HashShortener hash={to} link={`/addresses/${to}`} />
    </Table.Cell>
    <Table.Cell>
      <Text size="2" style={{ fontFamily: 'monospace' }}>
        {value}
      </Text>
    </Table.Cell>
    <Table.Cell>
      <Text size="2" color="gray">
        {time}
      </Text>
    </Table.Cell>
    <Table.Cell>
      <ViewButton to={`/transactions/${hash}`} />
    </Table.Cell>
  </Table.Row>
);

const AddressDetail: React.FC = () => {
  const { address } = useParams<{ address: string }>();

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState<ChipType>('list');

  // Fetch address instrumentation data
  const { data: addressData } = useGetAddressQuery({
    variables: { address: address ?? '' },
    skip: !address,
  });

  // TODO: Fetch actual data from API
  const loading = false;

  // Compute coverage from real data
  const coverage = {
    lists: addressData?.address.lists.totalCount ?? 0,
    alerts: 0, // TODO: implement when alerts are linked to addresses
    workflows: 0, // TODO: implement when workflows are linked to addresses
  };

  // Transform lists to InstrumentationItem format
  const listItems: InstrumentationItem[] =
    addressData?.address.lists.edges.map((edge) => ({
      id: edge.node.id,
      name: edge.node.name,
      description: edge.node.description ?? undefined,
      createdAt: edge.node.createdAt,
      status: edge.node.status === 'ACTIVE' ? 'active' : 'paused',
    })) ?? [];

  const openDialog = (type: ChipType) => {
    setDialogType(type);
    setDialogOpen(true);
  };

  const handleListsClick = () => openDialog('list');
  const handleAlertsClick = () => openDialog('alert');
  const handleWorkflowsClick = () => openDialog('workflow');

  if (!address) {
    return (
      <Flex align="center" justify="center" p="6">
        <Text color="red">No address provided</Text>
      </Flex>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box pb="2" px="1">
        <Flex align="center" justify="between" wrap="wrap" gap="3">
          <Flex align="center" gap="3">
            <Box
              style={{
                padding: '8px',
                borderRadius: '8px',
                backgroundColor: 'var(--accent-3)',
              }}
            >
              <PersonIcon width={20} height={20} />
            </Box>
            <Box>
              <Flex align="center" gap="2">
                <Heading as="h1" size="5" weight="bold">
                  {addressData?.address.entity?.name ?? 'Address'}
                </Heading>
                <Badge color="gray" variant="soft" size="1">
                  EOA
                </Badge>
              </Flex>
              <Flex align="center" gap="2" mt="1">
                <Code size="2">{address}</Code>
                <CopyButton text={address} />
              </Flex>
            </Box>
          </Flex>

          <Flex align="center" gap="3">
            <InstrumentationChipGroup
              coverage={coverage}
              showAddChips
              onListsClick={handleListsClick}
              onAlertsClick={handleAlertsClick}
              onWorkflowsClick={handleWorkflowsClick}
            />
          </Flex>
        </Flex>
      </Box>

      {/* Tabbed Content */}
      <Box px="1" pb="1" pt="2">
        <Tabs.Root defaultValue="overview">
          <Tabs.List size="2">
            <Tabs.Trigger value="overview">
              <Flex align="center" gap="2">
                <CubeIcon width="16" height="16" />
                Overview
              </Flex>
            </Tabs.Trigger>
            <Tabs.Trigger value="transactions">
              <Flex align="center" gap="2">
                <ActivityLogIcon width="16" height="16" />
                Transactions
              </Flex>
            </Tabs.Trigger>
            <Tabs.Trigger value="tokens">
              <Flex align="center" gap="2">
                <TokensIcon width="16" height="16" />
                Token Balances
              </Flex>
            </Tabs.Trigger>
            <Tabs.Trigger value="analytics">
              <Flex align="center" gap="2">
                <BarChartIcon width="16" height="16" />
                Analytics
              </Flex>
            </Tabs.Trigger>
            <Tabs.Trigger value="contracts">
              <Flex align="center" gap="2">
                <FileTextIcon width="16" height="16" />
                Contract Interactions
              </Flex>
            </Tabs.Trigger>
          </Tabs.List>

          <Box mt="3">
            {/* Overview Tab */}
            <Tabs.Content value="overview">
              <Grid columns={{ initial: '1', md: '2' }} gap="3">
                {/* Address Overview Card */}
                <MetadataCard
                  title="Address Overview"
                  icon={<PersonIcon />}
                  gradient={true}
                >
                  <MetadataItem label="Address" icon={<CopyIcon />}>
                    <HashShortener hash={address} />
                  </MetadataItem>

                  <MetadataItem label="Type" icon={<CubeIcon />}>
                    <Flex align="center" gap="2">
                      <Text size="2">Externally Owned Account</Text>
                      <Badge color="blue" variant="soft" size="1">
                        EOA
                      </Badge>
                    </Flex>
                  </MetadataItem>

                  <MetadataItem label="ETH Balance" icon={<TokensIcon />}>
                    {loading ? (
                      <Skeleton width="100px" height="16px" />
                    ) : (
                      <Flex align="center" gap="2">
                        <Text size="2" weight="bold">
                          0.00 ETH
                        </Text>
                        <Text size="1" color="gray">
                          ($0.00)
                        </Text>
                      </Flex>
                    )}
                  </MetadataItem>

                  <MetadataItem label="Token Holdings" icon={<PieChartIcon />}>
                    {loading ? (
                      <Skeleton width="80px" height="16px" />
                    ) : (
                      <Text size="2">0 tokens</Text>
                    )}
                  </MetadataItem>

                  <MetadataItem label="First Seen" icon={<ClockIcon />}>
                    {loading ? (
                      <Skeleton width="120px" height="16px" />
                    ) : (
                      <Text size="2" color="gray">
                        Not available
                      </Text>
                    )}
                  </MetadataItem>

                  <MetadataItem label="Last Active" icon={<ClockIcon />}>
                    {loading ? (
                      <Skeleton width="120px" height="16px" />
                    ) : (
                      <Text size="2" color="gray">
                        Not available
                      </Text>
                    )}
                  </MetadataItem>
                </MetadataCard>

                {/* Activity Summary Card */}
                <MetadataCard
                  title="Activity Summary"
                  icon={<ActivityLogIcon />}
                >
                  <MetadataItem label="Total Transactions" icon={<MixIcon />}>
                    {loading ? (
                      <Skeleton width="60px" height="16px" />
                    ) : (
                      <Text size="2">0</Text>
                    )}
                  </MetadataItem>

                  <MetadataItem label="Outgoing" icon={<ArrowUpIcon />}>
                    {loading ? (
                      <Skeleton width="60px" height="16px" />
                    ) : (
                      <Flex align="center" gap="2">
                        <Text size="2">0</Text>
                        <Badge color="red" variant="soft" size="1">
                          0 ETH
                        </Badge>
                      </Flex>
                    )}
                  </MetadataItem>

                  <MetadataItem label="Incoming" icon={<ArrowDownIcon />}>
                    {loading ? (
                      <Skeleton width="60px" height="16px" />
                    ) : (
                      <Flex align="center" gap="2">
                        <Text size="2">0</Text>
                        <Badge color="green" variant="soft" size="1">
                          0 ETH
                        </Badge>
                      </Flex>
                    )}
                  </MetadataItem>

                  <MetadataItem label="Unique Addresses" icon={<PersonIcon />}>
                    {loading ? (
                      <Skeleton width="60px" height="16px" />
                    ) : (
                      <Text size="2">0</Text>
                    )}
                  </MetadataItem>

                  <MetadataItem
                    label="Contract Interactions"
                    icon={<FileTextIcon />}
                  >
                    {loading ? (
                      <Skeleton width="60px" height="16px" />
                    ) : (
                      <Text size="2">0</Text>
                    )}
                  </MetadataItem>

                  <MetadataItem label="NFT Transfers" icon={<TokensIcon />}>
                    {loading ? (
                      <Skeleton width="60px" height="16px" />
                    ) : (
                      <Text size="2">0</Text>
                    )}
                  </MetadataItem>
                </MetadataCard>
              </Grid>

              {/* Balance History Chart */}
              <Card size="2" mt="3">
                <Heading as="h3" size="3" mb="3">
                  <Flex align="center" gap="2">
                    <BarChartIcon />
                    Balance History
                  </Flex>
                </Heading>
                <PlaceholderChart
                  height={250}
                  label="Balance history chart coming soon"
                />
              </Card>
            </Tabs.Content>

            {/* Transactions Tab */}
            <Tabs.Content value="transactions">
              <Card size="2">
                <Heading as="h3" size="3" mb="3">
                  <Flex align="center" gap="2">
                    <ActivityLogIcon />
                    Recent Transactions
                  </Flex>
                </Heading>
                <Table.Root size="1">
                  <Table.Header>
                    <Table.Row>
                      <Table.ColumnHeaderCell>Tx Hash</Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell>Type</Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell>From</Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell>To</Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell>Value</Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell>Time</Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell width="60px" />
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {/* Placeholder rows */}
                    <PlaceholderTxRow
                      hash="0xabc123def456789012345678901234567890123456789012345678901234def456"
                      type="out"
                      from={address}
                      to="0x742d35Cc6634C0532925a3b844Bc454e4438f44e"
                      value="0.5 ETH"
                      time="2 mins ago"
                    />
                    <PlaceholderTxRow
                      hash="0x789abc123def456789012345678901234567890123456789012345678901234567"
                      type="in"
                      from="0xdAC17F958D2ee523a2206206994597C13D831ec7"
                      to={address}
                      value="1.2 ETH"
                      time="15 mins ago"
                    />
                    <PlaceholderTxRow
                      hash="0xdef789abc012345678901234567890123456789012345678901234567890123456"
                      type="out"
                      from={address}
                      to="0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2"
                      value="0.1 ETH"
                      time="1 hour ago"
                    />
                  </Table.Body>
                </Table.Root>
                <Flex justify="center" mt="3">
                  <Text size="2" color="gray">
                    Showing placeholder data. Connect to API for real
                    transactions.
                  </Text>
                </Flex>
              </Card>
            </Tabs.Content>

            {/* Token Balances Tab */}
            <Tabs.Content value="tokens">
              <Grid columns={{ initial: '1', lg: '3' }} gap="3">
                {/* Portfolio Value Card */}
                <Card size="2">
                  <Heading as="h3" size="3" mb="2">
                    Total Portfolio Value
                  </Heading>
                  <Text size="7" weight="bold">
                    $0.00
                  </Text>
                  <Flex align="center" gap="1" mt="1">
                    <ArrowUpIcon color="var(--green-11)" />
                    <Text size="2" color="green">
                      +0.00%
                    </Text>
                    <Text size="2" color="gray">
                      (24h)
                    </Text>
                  </Flex>
                </Card>

                {/* Portfolio Breakdown */}
                <Card size="2" style={{ gridColumn: 'span 2' }}>
                  <Heading as="h3" size="3" mb="3">
                    <Flex align="center" gap="2">
                      <PieChartIcon />
                      Portfolio Breakdown
                    </Flex>
                  </Heading>
                  <PlaceholderChart
                    height={120}
                    label="Portfolio pie chart coming soon"
                  />
                </Card>
              </Grid>

              {/* Token List */}
              <Card size="2" mt="3">
                <Heading as="h3" size="3" mb="3">
                  <Flex align="center" gap="2">
                    <TokensIcon />
                    Token Holdings
                  </Flex>
                </Heading>
                <Table.Root size="1">
                  <Table.Header>
                    <Table.Row>
                      <Table.ColumnHeaderCell>Token</Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell>Balance</Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell>Value</Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell>
                        24h Change
                      </Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell width="70px" />
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {/* Placeholder token rows */}
                    <PlaceholderTokenRow
                      symbol="ETH"
                      name="Ethereum"
                      balance="0.000"
                      value="$0.00"
                      change={0}
                      onMonitor={() => openDialog('alert')}
                    />
                    <PlaceholderTokenRow
                      symbol="USDT"
                      name="Tether USD"
                      balance="0.00"
                      value="$0.00"
                      change={0}
                      contractAddress="0xdAC17F958D2ee523a2206206994597C13D831ec7"
                      onMonitor={() => openDialog('alert')}
                    />
                    <PlaceholderTokenRow
                      symbol="USDC"
                      name="USD Coin"
                      balance="0.00"
                      value="$0.00"
                      change={0}
                      contractAddress="0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48"
                      onMonitor={() => openDialog('alert')}
                    />
                  </Table.Body>
                </Table.Root>
                <Flex justify="center" mt="3">
                  <Text size="2" color="gray">
                    Showing placeholder data. Connect to API for real token
                    balances.
                  </Text>
                </Flex>
              </Card>
            </Tabs.Content>

            {/* Analytics Tab */}
            <Tabs.Content value="analytics">
              <Grid columns={{ initial: '1', md: '2' }} gap="3">
                <Card size="2">
                  <Heading as="h3" size="3" mb="3">
                    <Flex align="center" gap="2">
                      <BarChartIcon />
                      Transaction Volume
                    </Flex>
                  </Heading>
                  <PlaceholderChart
                    height={200}
                    label="Transaction volume chart coming soon"
                  />
                </Card>

                <Card size="2">
                  <Heading as="h3" size="3" mb="3">
                    <Flex align="center" gap="2">
                      <ActivityLogIcon />
                      Gas Spent Over Time
                    </Flex>
                  </Heading>
                  <PlaceholderChart
                    height={200}
                    label="Gas usage chart coming soon"
                  />
                </Card>

                <Card size="2">
                  <Heading as="h3" size="3" mb="3">
                    <Flex align="center" gap="2">
                      <PersonIcon />
                      Top Interacted Addresses
                    </Flex>
                  </Heading>
                  <PlaceholderChart
                    height={200}
                    label="Address interaction chart coming soon"
                  />
                </Card>

                <Card size="2">
                  <Heading as="h3" size="3" mb="3">
                    <Flex align="center" gap="2">
                      <ClockIcon />
                      Activity Heatmap
                    </Flex>
                  </Heading>
                  <PlaceholderChart
                    height={200}
                    label="Activity heatmap coming soon"
                  />
                </Card>
              </Grid>
            </Tabs.Content>

            {/* Contract Interactions Tab */}
            <Tabs.Content value="contracts">
              <Card size="2">
                <Heading as="h3" size="3" mb="3">
                  <Flex align="center" gap="2">
                    <FileTextIcon />
                    Contract Interactions
                  </Flex>
                </Heading>
                <Table.Root size="1">
                  <Table.Header>
                    <Table.Row>
                      <Table.ColumnHeaderCell>Contract</Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell>Name</Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell>
                        Interactions
                      </Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell>Last Used</Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell width="60px" />
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    <Table.Row>
                      <Table.Cell>
                        <HashShortener
                          hash="0xdAC17F958D2ee523a2206206994597C13D831ec7"
                          link="/contracts/0xdAC17F958D2ee523a2206206994597C13D831ec7"
                        />
                      </Table.Cell>
                      <Table.Cell>
                        <Text size="2">Tether USD</Text>
                      </Table.Cell>
                      <Table.Cell>
                        <Text size="2">0</Text>
                      </Table.Cell>
                      <Table.Cell>
                        <Text size="2" color="gray">
                          Never
                        </Text>
                      </Table.Cell>
                      <Table.Cell>
                        <Badge color="green" variant="soft" size="1">
                          <Flex align="center" gap="1">
                            <CheckCircledIcon />
                            Verified
                          </Flex>
                        </Badge>
                      </Table.Cell>
                      <Table.Cell>
                        <ViewButton to="/contracts/0xdAC17F958D2ee523a2206206994597C13D831ec7" />
                      </Table.Cell>
                    </Table.Row>
                    <Table.Row>
                      <Table.Cell>
                        <HashShortener
                          hash="0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2"
                          link="/contracts/0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2"
                        />
                      </Table.Cell>
                      <Table.Cell>
                        <Text size="2">Wrapped Ether</Text>
                      </Table.Cell>
                      <Table.Cell>
                        <Text size="2">0</Text>
                      </Table.Cell>
                      <Table.Cell>
                        <Text size="2" color="gray">
                          Never
                        </Text>
                      </Table.Cell>
                      <Table.Cell>
                        <Badge color="green" variant="soft" size="1">
                          <Flex align="center" gap="1">
                            <CheckCircledIcon />
                            Verified
                          </Flex>
                        </Badge>
                      </Table.Cell>
                      <Table.Cell>
                        <ViewButton to="/contracts/0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2" />
                      </Table.Cell>
                    </Table.Row>
                  </Table.Body>
                </Table.Root>
                <Flex justify="center" mt="3">
                  <Text size="2" color="gray">
                    Showing placeholder data. Connect to API for real contract
                    interactions.
                  </Text>
                </Flex>
              </Card>
            </Tabs.Content>
          </Box>
        </Tabs.Root>
      </Box>

      {/* Instrumentation Panel */}
      <InstrumentationPanel
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        type={dialogType}
        entityAddress={address}
        items={dialogType === 'list' ? listItems : undefined}
      />
    </Box>
  );
};

export default AddressDetail;
